import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';

// Legacy localStorage key — read once on first API load, push to DB, then deleted.
const LEGACY_STORAGE_KEY = 'channel-strategy-v1';

// Default seed data. Users can edit everything and changes persist to localStorage.
const DEFAULT_CHANNELS = [
  {
    id: 'pc',
    name: 'P&C',
    description: 'Petrol & Convenience',
    objectives: [
      'Build a profitable, impulse-led presence for Infuzions and Bhuja across major P&C networks',
      'Secure permanent ranging in top 3 networks (7-Eleven, Ampol/EG, Coles Express) before FY27',
      'Grow P&C channel revenue from niche to $1M+ annual GSV within 24 months',
      'Use P&C ranging as a trial channel for NPD before WW/Coles range reviews',
    ],
    customers: [
      {
        id: 'seven-eleven',
        name: '7-Eleven',
        sourceOfSupply: 'Direct to 7-Eleven DC (Truganina VIC, Eastern Creek NSW)',
        annualGsv: 450000,
        skuDistribution: 'Infuzions Veggie Straws 75g, Bhuja Original Mix 100g',
        numberOfStores: 750,
        promoPeriods: 13,
        opportunities: [
          { id: 'opp1', description: 'Add Bhuja Cracker Mix 100g to impulse bay', gsvEstimate: 180000 },
          { id: 'opp2', description: 'Seasonal summer cricket promo pack', gsvEstimate: 90000 },
        ],
        promoProgram: [
          { id: 'pp1', assortment: 'Infuzions Veggie Straws 75g', mechanic: '2 for $5', periods: 6 },
          { id: 'pp2', assortment: 'Bhuja Original Mix 100g', mechanic: '$3.50 price point', periods: 4 },
        ],
        customerTypes: [
          'Young male tradies (18-35) on morning fuel run — impulse snack with coffee',
          'CBD office workers buying lunch add-on',
          'Late-night top-up shoppers — higher price tolerance',
          'Cross-shop with Coles/Woolworths for pantry; P&C is impulse-only',
        ],
        keyDates: [
          { id: 'kd1', event: 'Salty Snacks Range Review', date: '2026-08-15' },
          { id: 'kd2', event: 'Summer Promo Window Submission', date: '2026-09-30' },
        ],
      },
      {
        id: 'ampol-eg',
        name: 'Ampol / EG Australia',
        sourceOfSupply: 'Via Metcash Convenience or direct via Ampol DC (Lytton QLD)',
        annualGsv: 220000,
        skuDistribution: 'Infuzions Veggie Straws 75g only',
        numberOfStores: 690,
        promoPeriods: 12,
        opportunities: [
          { id: 'opp1', description: 'Range Bhuja Original Mix 100g in Foodary format', gsvEstimate: 140000 },
          { id: 'opp2', description: 'On The Run (OTR) SA network expansion', gsvEstimate: 75000 },
        ],
        promoProgram: [
          { id: 'pp1', assortment: 'Infuzions Veggie Straws 75g', mechanic: '$3.00 price point', periods: 4 },
        ],
        customerTypes: [
          'Highway travellers — family snacking on road trips',
          'Tradies on job sites, bulk-snack purchase',
          'Foodary format attracts convenience grocery shopper',
        ],
        keyDates: [
          { id: 'kd1', event: 'Ampol Range Review', date: '2026-06-01' },
          { id: 'kd2', event: 'EG Category Review', date: '2026-09-15' },
        ],
      },
      {
        id: 'coles-express',
        name: 'Coles Express (Viva Energy)',
        sourceOfSupply: 'Via Coles supply chain — leverage existing Coles relationship',
        annualGsv: 180000,
        skuDistribution: 'Not currently ranged',
        numberOfStores: 710,
        promoPeriods: 13,
        opportunities: [
          { id: 'opp1', description: 'Secure initial listing — 2 SKUs impulse format', gsvEstimate: 180000 },
          { id: 'opp2', description: 'Flybuys integration for targeted offers', gsvEstimate: 40000 },
        ],
        promoProgram: [],
        customerTypes: [
          'Commuter fuel-and-go shopper',
          'Younger skew vs Ampol — 25-45 family and single',
          'Price-sensitive, responds to multi-buy mechanics',
        ],
        keyDates: [
          { id: 'kd1', event: 'Coles Express Range Review', date: '2026-07-20' },
        ],
      },
    ],
  },
];

// ── legacy storage helper (one-time migration only) ─────────────
function loadLegacyChannels() {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
}

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

function fmtCurrency(n) {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
}

// ── small UI primitives ─────────────────────────────────────────
const LABEL_STYLE = {
  fontSize: 10,
  color: 'var(--text-muted)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 4,
};

const INPUT_STYLE = {
  width: '100%',
  background: 'var(--bg-input)',
  border: '1px solid var(--border-soft)',
  borderRadius: 6,
  padding: '6px 8px',
  color: 'var(--text-primary)',
  fontSize: 13,
  fontFamily: 'inherit',
};

const BTN_PRIMARY = {
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '6px 12px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

const BTN_GHOST = {
  background: 'transparent',
  color: 'var(--text-muted)',
  border: '1px solid var(--border-soft)',
  borderRadius: 6,
  padding: '4px 10px',
  fontSize: 11,
  cursor: 'pointer',
};

const BTN_DANGER = {
  background: 'transparent',
  color: 'var(--danger)',
  border: '1px solid var(--danger-border)',
  borderRadius: 6,
  padding: '2px 8px',
  fontSize: 11,
  cursor: 'pointer',
};

function Field({ label, children }) {
  return (
    <div>
      <div style={LABEL_STYLE}>{label}</div>
      {children}
    </div>
  );
}

function SectionHeader({ title, onAdd, addLabel = 'Add' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
      <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {title}
      </div>
      {onAdd && (
        <button style={BTN_GHOST} onClick={onAdd}>+ {addLabel}</button>
      )}
    </div>
  );
}

// ── Objectives list (channel-level) ─────────────────────────────
function ObjectivesEditor({ objectives, onChange }) {
  const add = () => onChange([...objectives, '']);
  const update = (i, v) => onChange(objectives.map((o, idx) => (idx === i ? v : o)));
  const remove = (i) => onChange(objectives.filter((_, idx) => idx !== i));

  return (
    <div className="card">
      <SectionHeader title="Channel Objectives" onAdd={add} addLabel="Objective" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {objectives.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            No objectives yet. Click "+ Objective" to add one.
          </div>
        )}
        {objectives.map((obj, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <span style={{
              fontSize: 11, color: 'var(--accent)', fontWeight: 700,
              minWidth: 20, paddingTop: 8,
            }}>
              {i + 1}.
            </span>
            <textarea
              value={obj}
              onChange={(e) => update(i, e.target.value)}
              rows={1}
              style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 32 }}
              placeholder="Describe an objective for this channel…"
            />
            <button style={BTN_DANGER} onClick={() => remove(i)} title="Remove">✕</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Customer card (collapsible) ─────────────────────────────────
function CustomerCard({ customer, onChange, onRemove }) {
  const [expanded, setExpanded] = useState(true);

  const update = (patch) => onChange({ ...customer, ...patch });

  // opportunities
  const addOpportunity = () => update({
    opportunities: [...customer.opportunities, { id: uid(), description: '', gsvEstimate: 0 }],
  });
  const updateOpportunity = (id, patch) => update({
    opportunities: customer.opportunities.map(o => (o.id === id ? { ...o, ...patch } : o)),
  });
  const removeOpportunity = (id) => update({
    opportunities: customer.opportunities.filter(o => o.id !== id),
  });

  // promo program
  const addPromo = () => update({
    promoProgram: [...customer.promoProgram, { id: uid(), assortment: '', mechanic: '', periods: 0 }],
  });
  const updatePromo = (id, patch) => update({
    promoProgram: customer.promoProgram.map(p => (p.id === id ? { ...p, ...patch } : p)),
  });
  const removePromo = (id) => update({
    promoProgram: customer.promoProgram.filter(p => p.id !== id),
  });

  // customer types
  const addType = () => update({ customerTypes: [...customer.customerTypes, ''] });
  const updateType = (i, v) => update({
    customerTypes: customer.customerTypes.map((t, idx) => (idx === i ? v : t)),
  });
  const removeType = (i) => update({
    customerTypes: customer.customerTypes.filter((_, idx) => idx !== i),
  });

  // key dates
  const addKeyDate = () => update({
    keyDates: [...customer.keyDates, { id: uid(), event: '', date: '' }],
  });
  const updateKeyDate = (id, patch) => update({
    keyDates: customer.keyDates.map(k => (k.id === id ? { ...k, ...patch } : k)),
  });
  const removeKeyDate = (id) => update({
    keyDates: customer.keyDates.filter(k => k.id !== id),
  });

  const totalPromoPeriodsUsed = customer.promoProgram.reduce((sum, p) => sum + (Number(p.periods) || 0), 0);
  const totalOpportunityGsv = customer.opportunities.reduce((sum, o) => sum + (Number(o.gsvEstimate) || 0), 0);

  return (
    <div className="card" style={{ padding: 0 }}>
      {/* Header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', borderBottom: expanded ? '1px solid var(--border)' : 'none',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{expanded ? '▾' : '▸'}</span>
          <input
            value={customer.name}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => update({ name: e.target.value })}
            style={{
              ...INPUT_STYLE,
              background: 'transparent',
              border: 'none',
              padding: 0,
              fontSize: 15,
              fontWeight: 700,
              color: 'var(--text-primary)',
              width: 260,
            }}
          />
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {customer.numberOfStores ? `${Number(customer.numberOfStores).toLocaleString()} stores` : '—'}
            {' · '}
            Est GSV {fmtCurrency(customer.annualGsv)}
          </span>
        </div>
        <button
          style={BTN_DANGER}
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          title="Remove customer"
        >
          Remove
        </button>
      </div>

      {expanded && (
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Top row — source, GSV, SKU distribution, stores */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1.3fr 0.7fr', gap: 12 }}>
            <Field label="Source of Supply">
              <input
                value={customer.sourceOfSupply}
                onChange={(e) => update({ sourceOfSupply: e.target.value })}
                style={INPUT_STYLE}
                placeholder="e.g. Direct DC, via Metcash, 3PL…"
              />
            </Field>
            <Field label="Estimated Annual GSV">
              <input
                type="number"
                value={customer.annualGsv}
                onChange={(e) => update({ annualGsv: Number(e.target.value) || 0 })}
                style={INPUT_STYLE}
                placeholder="0"
              />
            </Field>
            <Field label="Current SKU Distribution">
              <input
                value={customer.skuDistribution}
                onChange={(e) => update({ skuDistribution: e.target.value })}
                style={INPUT_STYLE}
                placeholder="SKUs currently ranged"
              />
            </Field>
            <Field label="Number of Stores">
              <input
                type="number"
                value={customer.numberOfStores}
                onChange={(e) => update({ numberOfStores: Number(e.target.value) || 0 })}
                style={INPUT_STYLE}
                placeholder="0"
              />
            </Field>
          </div>

          {/* Promo periods selector */}
          <div>
            <div style={LABEL_STYLE}>Promo Periods Per Year</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { n: 12, label: '12 periods'  },
                { n: 13, label: '13 periods'  },
                { n: 52, label: '52 weeks'    },
              ].map(({ n, label }) => (
                <button
                  key={n}
                  onClick={() => update({ promoPeriods: n })}
                  style={{
                    ...BTN_GHOST,
                    padding: '6px 16px',
                    background: customer.promoPeriods === n ? 'var(--accent)' : 'transparent',
                    color: customer.promoPeriods === n ? '#fff' : 'var(--text-body)',
                    borderColor: customer.promoPeriods === n ? 'var(--accent)' : 'var(--border-soft)',
                    fontWeight: customer.promoPeriods === n ? 600 : 400,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Two columns: opportunities + promo program */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Opportunities */}
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
              <SectionHeader
                title={`Opportunities · ${fmtCurrency(totalOpportunityGsv)} upside`}
                onAdd={addOpportunity}
                addLabel="Opportunity"
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {customer.opportunities.length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    No opportunities added yet.
                  </div>
                )}
                {customer.opportunities.map(o => (
                  <div key={o.id} style={{ display: 'grid', gridTemplateColumns: '1fr 110px auto', gap: 6, alignItems: 'center' }}>
                    <input
                      value={o.description}
                      onChange={(e) => updateOpportunity(o.id, { description: e.target.value })}
                      style={INPUT_STYLE}
                      placeholder="Opportunity description"
                    />
                    <input
                      type="number"
                      value={o.gsvEstimate}
                      onChange={(e) => updateOpportunity(o.id, { gsvEstimate: Number(e.target.value) || 0 })}
                      style={INPUT_STYLE}
                      placeholder="GSV est. $"
                    />
                    <button style={BTN_DANGER} onClick={() => removeOpportunity(o.id)}>✕</button>
                  </div>
                ))}
              </div>
            </div>

            {/* Basic Promotional Program */}
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
              <SectionHeader
                title={`Promo Program · ${totalPromoPeriodsUsed}/${customer.promoPeriods || 0} periods`}
                onAdd={addPromo}
                addLabel="Program"
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {customer.promoProgram.length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    No promo programs yet.
                  </div>
                )}
                {customer.promoProgram.map(p => (
                  <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 70px auto', gap: 6, alignItems: 'center' }}>
                    <input
                      value={p.assortment}
                      onChange={(e) => updatePromo(p.id, { assortment: e.target.value })}
                      style={INPUT_STYLE}
                      placeholder="Assortment / SKU group"
                    />
                    <input
                      value={p.mechanic}
                      onChange={(e) => updatePromo(p.id, { mechanic: e.target.value })}
                      style={INPUT_STYLE}
                      placeholder="Mechanic (e.g. 2 for $5)"
                    />
                    <input
                      type="number"
                      min="0"
                      max={customer.promoPeriods || 13}
                      value={p.periods}
                      onChange={(e) => updatePromo(p.id, { periods: Number(e.target.value) || 0 })}
                      style={INPUT_STYLE}
                      placeholder="#"
                      title="Periods out of selected plan"
                    />
                    <button style={BTN_DANGER} onClick={() => removePromo(p.id)}>✕</button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Two columns: customer types + key dates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Customer types */}
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
              <SectionHeader
                title="Customer Types (Range Review Targeting)"
                onAdd={addType}
                addLabel="Type"
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {customer.customerTypes.length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    No customer types yet.
                  </div>
                )}
                {customer.customerTypes.map((t, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <textarea
                      value={t}
                      onChange={(e) => updateType(i, e.target.value)}
                      rows={1}
                      style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 32 }}
                      placeholder="Describe a shopper segment we target…"
                    />
                    <button style={BTN_DANGER} onClick={() => removeType(i)}>✕</button>
                  </div>
                ))}
              </div>
            </div>

            {/* Key dates */}
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
              <SectionHeader title="Key Dates" onAdd={addKeyDate} addLabel="Date" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {customer.keyDates.length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    No key dates yet (e.g. Salty Snacks Range Review).
                  </div>
                )}
                {customer.keyDates
                  .slice()
                  .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
                  .map(k => (
                    <div key={k.id} style={{ display: 'grid', gridTemplateColumns: '1fr 140px auto', gap: 6, alignItems: 'center' }}>
                      <input
                        value={k.event}
                        onChange={(e) => updateKeyDate(k.id, { event: e.target.value })}
                        style={INPUT_STYLE}
                        placeholder="Event name (e.g. Salty Snacks Range Review)"
                      />
                      <input
                        type="date"
                        value={k.date}
                        onChange={(e) => updateKeyDate(k.id, { date: e.target.value })}
                        style={INPUT_STYLE}
                      />
                      <button style={BTN_DANGER} onClick={() => removeKeyDate(k.id)}>✕</button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────
export default function ChannelStrategy() {
  const apiFetch = useApi();

  // Local editable state. Initialised to DEFAULT_CHANNELS; replaced once
  // the API responds with the user's saved version (or kept if API has no row).
  const [channels, setChannels] = useState(DEFAULT_CHANNELS);
  const [activeId, setActiveId] = useState(DEFAULT_CHANNELS[0]?.id || 'pc');
  const hasHydrated = useRef(false);

  // Initial load from API.
  const {
    data: serverData,
    error: serverError,
    refetch: refetchStrategy,
    isFetching: isLoadingStrategy,
    isLoading,
  } = useQuery({
    queryKey: ['channel-strategy'],
    queryFn: async () => {
      const res = await apiFetch('/channel-strategy');
      if (!res.ok) throw new Error(`Failed to load channel strategy: ${res.status}`);
      return res.json();
    },
    refetchOnMount: 'always',
    // Don't auto-retry on hard failures — surface them so the user can act
    // (reload, re-auth). Without this, hydration silently fails and the
    // save effect short-circuits forever, with no visible feedback.
    retry: false,
  });

  // Debounced save via PUT — fires 1s after the last edit lands.
  const saveStrategy = useMutation({
    mutationFn: async (next) => {
      const res = await apiFetch('/channel-strategy', {
        method: 'PUT',
        body: JSON.stringify({ strategy: next }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`Save failed: HTTP ${res.status} ${body.slice(0, 200)}`);
      }
      return res.json();
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error('[ChannelStrategy] save error', err);
    },
  });

  // Stable ref to the mutation's mutate function. The mutation OBJECT
  // identity changes on every render (which would re-fire effects below
  // and cause a PUT loop) — but the underlying mutate function is logically
  // stable. Capture it once via a ref that we update each render.
  const mutateRef = useRef(saveStrategy.mutate);
  mutateRef.current = saveStrategy.mutate;

  // Tracks the JSON of the last value we either received from the server
  // OR successfully saved to it. The debounced-save effect compares
  // `channels` against this — if equal, the save is a no-op and skipped.
  // This prevents the "hydrate-then-immediately-save-the-same-data" loop.
  const lastSavedJsonRef = useRef(null);

  // Hydrate local state from API response (runs once on first successful load).
  useEffect(() => {
    if (hasHydrated.current || !serverData) return;
    hasHydrated.current = true;

    if (serverData.strategy) {
      setChannels(serverData.strategy);
      setActiveId(serverData.strategy[0]?.id || 'pc');
      lastSavedJsonRef.current = JSON.stringify(serverData.strategy);
      return;
    }

    // No DB row yet — try to migrate from localStorage (one-shot).
    const legacy = loadLegacyChannels();
    if (legacy) {
      setChannels(legacy);
      setActiveId(legacy[0]?.id || 'pc');
      const legacyJson = JSON.stringify(legacy);
      lastSavedJsonRef.current = legacyJson;
      mutateRef.current(legacy, {
        onSuccess: () => {
          try { localStorage.removeItem(LEGACY_STORAGE_KEY); } catch { /* ignore */ }
        },
      });
    } else {
      // No DB, no legacy — keep DEFAULT_CHANNELS in state. Mark it as
      // the baseline so the save effect doesn't fire until the user edits.
      lastSavedJsonRef.current = JSON.stringify(DEFAULT_CHANNELS);
    }
  }, [serverData]);

  // Debounced save: schedule a PUT 1s after the last `channels` change.
  // Skip if hasn't hydrated yet OR the value matches what's already saved.
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!hasHydrated.current) return;
    const currentJson = JSON.stringify(channels);
    if (currentJson === lastSavedJsonRef.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      mutateRef.current(channels);
      lastSavedJsonRef.current = currentJson;
    }, 1000);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [channels]);

  // Drag-and-drop state for the channel tab bar. dragIndex is the tab being
  // dragged; dropTarget is the index where it will land if released now.
  const [dragIndex, setDragIndex] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const moveChannel = (fromIdx, toIdx) => {
    setChannels(prev => {
      if (fromIdx < 0 || fromIdx >= prev.length) return prev;
      const next = [...prev];
      const [picked] = next.splice(fromIdx, 1);
      const insertAt = Math.max(0, Math.min(next.length, toIdx > fromIdx ? toIdx - 1 : toIdx));
      next.splice(insertAt, 0, picked);
      return next;
    });
  };

  // (Loading state shown inline below — early-return here would skip useMemo
  // and violate the rules of hooks. Page renders DEFAULT_CHANNELS until
  // the API hydrates, which is acceptable: same data the user would see
  // on first visit anyway.)

  const active = channels.find(c => c.id === activeId) || channels[0];

  const updateActive = (patch) => {
    setChannels(chs => chs.map(c => (c.id === active.id ? { ...c, ...patch } : c)));
  };

  const addChannel = () => {
    const name = window.prompt('New channel name (e.g. Food Service, Export, Pharmacy):');
    if (!name) return;
    const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || `ch-${uid()}`;
    if (channels.some(c => c.id === id)) {
      window.alert('A channel with that id already exists.');
      return;
    }
    const newChannel = {
      id, name, description: '', objectives: [], customers: [],
    };
    setChannels([...channels, newChannel]);
    setActiveId(id);
  };

  const removeChannel = (id) => {
    if (channels.length <= 1) {
      window.alert('Keep at least one channel.');
      return;
    }
    if (!window.confirm('Remove this channel and all its data?')) return;
    const remaining = channels.filter(c => c.id !== id);
    setChannels(remaining);
    if (activeId === id) setActiveId(remaining[0].id);
  };

  const addCustomer = () => {
    const newCustomer = {
      id: uid(),
      name: 'New Customer',
      sourceOfSupply: '',
      annualGsv: 0,
      skuDistribution: '',
      numberOfStores: 0,
      promoPeriods: 13,
      opportunities: [],
      promoProgram: [],
      customerTypes: [],
      keyDates: [],
    };
    updateActive({ customers: [...active.customers, newCustomer] });
  };

  const updateCustomer = (customer) => {
    updateActive({
      customers: active.customers.map(c => (c.id === customer.id ? customer : c)),
    });
  };

  const removeCustomer = (id) => {
    if (!window.confirm('Remove this customer?')) return;
    updateActive({ customers: active.customers.filter(c => c.id !== id) });
  };

  const resetDefaults = () => {
    if (!window.confirm('Reset this channel to seeded defaults? This will discard your edits.')) return;
    const seed = DEFAULT_CHANNELS.find(c => c.id === active.id);
    if (!seed) {
      window.alert('No seed data for this channel.');
      return;
    }
    setChannels(chs => chs.map(c => (c.id === active.id ? seed : c)));
  };

  // Channel summary metrics
  const summary = useMemo(() => {
    const totalGsv = active.customers.reduce((s, c) => s + (Number(c.annualGsv) || 0), 0);
    const totalOpp = active.customers.reduce(
      (s, c) => s + c.opportunities.reduce((a, o) => a + (Number(o.gsvEstimate) || 0), 0),
      0,
    );
    const totalStores = active.customers.reduce((s, c) => s + (Number(c.numberOfStores) || 0), 0);
    return { totalGsv, totalOpp, totalStores, customerCount: active.customers.length };
  }, [active]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Load-failure banner: hydration never completed, so edits won't save.
          Without this, users see DEFAULT_CHANNELS, edit them, and silently
          lose their work because the save effect short-circuits until
          hydration succeeds. Matches the CRM.jsx Phase 14 fix. */}
      {serverError && !hasHydrated.current && (
        <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 'var(--radius)', color: 'var(--danger)', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13 }}>
            <strong>Could not load your channel strategy.</strong>{' '}
            <span>Any edits you make below will <u>not be saved</u> until this loads successfully.</span>
            <div style={{ fontSize: 11, marginTop: 4, opacity: 0.85 }}>
              {String(serverError?.message || serverError).slice(0, 200)}
            </div>
          </div>
          <button onClick={() => refetchStrategy()} disabled={isLoadingStrategy}
            style={{ background: 'var(--bg-card)', border: '1px solid var(--danger)', color: 'var(--danger)', borderRadius: 'var(--radius)', padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: isLoadingStrategy ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
            {isLoadingStrategy ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )}
      {/* Channel tab bar — drag any tab to reorder */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, borderBottom: '1px solid var(--border)' }}>
        {channels.map((c, idx) => {
          const dragging = dragIndex === idx;
          const dropBefore = dropTarget === idx;
          const dropAfter  = dropTarget === idx + 1 && idx === channels.length - 1;
          return (
            <button
              key={c.id}
              draggable
              onDragStart={(e) => {
                setDragIndex(idx);
                e.dataTransfer.effectAllowed = 'move';
                try { e.dataTransfer.setData('text/plain', String(idx)); } catch {}
              }}
              onDragOver={(e) => {
                if (dragIndex == null) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                const rect = e.currentTarget.getBoundingClientRect();
                const before = (e.clientX - rect.left) < rect.width / 2;
                setDropTarget(before ? idx : idx + 1);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragIndex != null && dropTarget != null && dragIndex !== dropTarget && dragIndex + 1 !== dropTarget) {
                  moveChannel(dragIndex, dropTarget);
                }
                setDragIndex(null); setDropTarget(null);
              }}
              onDragEnd={() => { setDragIndex(null); setDropTarget(null); }}
              onClick={() => setActiveId(c.id)}
              className={activeId === c.id ? 'tab-active' : 'tab-inactive'}
              title="Drag to reorder · Click to switch"
              style={{
                padding: '10px 18px',
                background: 'none',
                cursor: dragging ? 'grabbing' : 'grab',
                fontSize: 13,
                fontWeight: activeId === c.id ? 600 : 400,
                opacity: dragging ? 0.4 : 1,
                borderLeft:  dropBefore ? '2px solid var(--accent)' : '2px solid transparent',
                borderRight: dropAfter  ? '2px solid var(--accent)' : '2px solid transparent',
                transition: 'opacity 0.15s',
              }}
            >
              {c.name}
            </button>
          );
        })}
        <button
          onClick={addChannel}
          style={{
            ...BTN_GHOST,
            marginLeft: 8,
            padding: '6px 12px',
            borderStyle: 'dashed',
          }}
        >
          + Add Channel
        </button>
        <div style={{ flex: 1 }} />
        <button onClick={resetDefaults} style={BTN_GHOST} title="Reset to seeded defaults">
          Reset Defaults
        </button>
        <button onClick={() => removeChannel(active.id)} style={{ ...BTN_GHOST, color: 'var(--danger)', borderColor: 'var(--danger-border)' }}>
          Remove Channel
        </button>
      </div>

      {/* Channel header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <input
              value={active.name}
              onChange={(e) => updateActive({ name: e.target.value })}
              style={{
                ...INPUT_STYLE,
                fontSize: 22, fontWeight: 700, color: 'var(--text-primary)',
                background: 'transparent', border: 'none', padding: 0, width: 320,
              }}
            />
          </div>
          <input
            value={active.description}
            onChange={(e) => updateActive({ description: e.target.value })}
            style={{
              ...INPUT_STYLE,
              fontSize: 13, color: 'var(--text-muted)',
              background: 'transparent', border: 'none', padding: 0,
            }}
            placeholder="Short description for this channel…"
          />
        </div>

        {/* Summary stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, minWidth: 560 }}>
          <div className="kpi-card" style={{ padding: '10px 14px' }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Customers</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>{summary.customerCount}</div>
          </div>
          <div className="kpi-card" style={{ padding: '10px 14px' }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Total Stores</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>{summary.totalStores.toLocaleString()}</div>
          </div>
          <div className="kpi-card" style={{ padding: '10px 14px' }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Est Annual GSV</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>{fmtCurrency(summary.totalGsv)}</div>
          </div>
          <div className="kpi-card" style={{ padding: '10px 14px' }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Upside Pipeline</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--success)' }}>{fmtCurrency(summary.totalOpp)}</div>
          </div>
        </div>
      </div>

      {/* Objectives */}
      <ObjectivesEditor
        objectives={active.objectives}
        onChange={(next) => updateActive({ objectives: next })}
      />

      {/* Customers */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Customers in {active.name}
          </div>
          <button style={BTN_PRIMARY} onClick={addCustomer}>+ Add Customer</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {active.customers.length === 0 && (
            <div className="card" style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', textAlign: 'center', padding: 24 }}>
              No customers in this channel yet. Click "+ Add Customer" to start.
            </div>
          )}
          {active.customers.map(c => (
            <CustomerCard
              key={c.id}
              customer={c}
              onChange={updateCustomer}
              onRemove={() => removeCustomer(c.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
